import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Gift, Check, ArrowLeft } from 'lucide-react';
import { type Brand, type GiftAmount } from '@/utils/types';

const BRANDS: Brand[] = [
  { id: 'mcdonalds', name: 'McDonald\'s', logo: '/images/mcdonalds.png', silo: 'restaurants' },
  { id: 'burgerking', name: 'Burger King', logo: '/images/burgerking.png', silo: 'restaurants' },
  { id: 'wendys', name: 'Wendy\'s', logo: '/images/wendys.png', silo: 'restaurants' },
  { id: 'chickfila', name: 'Chick-fil-A', logo: '/lovable-uploads/4fd095b4-4941-485a-9297-abac0d37c069.png', silo: 'restaurants' },
  
  { id: 'dunkin', name: 'Dunkin\' Donuts', logo: '/images/dunkin.png', silo: 'beverages' },
  { id: 'starbucks', name: 'Starbucks', logo: '/images/starbucks.png', silo: 'beverages' },
  { id: 'krispy', name: 'Krispy Kreme', logo: '/images/krispy.png', silo: 'beverages' },
  { id: 'timhortons', name: 'Tim Horton\'s', logo: '/images/timhortons.png', silo: 'beverages' },
  
  { id: 'cvs', name: 'CVS', logo: '/images/cvs.png', silo: 'convenience' },
  { id: 'riteaid', name: 'Rite Aid', logo: '/images/riteaid.png', silo: 'convenience' },
  { id: 'walgreens', name: 'Walgreens', logo: '/images/walgreens.png', silo: 'convenience' },
  { id: 'walmart', name: 'Walmart', logo: '/images/walmart.png', silo: 'convenience' },
  
  { id: 'showcase', name: 'Showcase Cinemas', logo: '/images/showcase.png', silo: 'entertainment' },
  { id: 'amc', name: 'AMC Theatres', logo: '/images/amc.png', silo: 'entertainment' },
  { id: 'xbox', name: 'Xbox', logo: '/images/xbox.png', silo: 'entertainment' },
  { id: 'playstation', name: 'PlayStation', logo: '/images/playstation.png', silo: 'entertainment' },
];

type QuickGiftStep = 'brand-selection' | 'amount-selection' | 'message' | 'confirmation';

const QuickGift = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { recipientId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const specialDay = queryParams.get('specialDay');
  
  const [step, setStep] = useState<QuickGiftStep>('brand-selection');
  const [recipient, setRecipient] = useState<string>(recipientId || 'Carl'); // Default for demo
  const [recipientFavoriteBrands, setRecipientFavoriteBrands] = useState<string[]>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');
  const [selectedAmount, setSelectedAmount] = useState<GiftAmount>(5);
  const [message, setMessage] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  
  useEffect(() => {
    setRecipientFavoriteBrands(['chickfila', 'dunkin', 'walmart', 'amc']);
    
    if (specialDay) {
      setMessage(`Happy ${specialDay}! Hope you enjoy this little treat.`);
    }
  }, [specialDay]);
  
  const handleBrandSelect = (brandId: string) => {
    setSelectedBrandId(brandId);
    setStep('amount-selection');
  };
  
  const handleAmountSelect = (amount: GiftAmount) => {
    setSelectedAmount(amount);
    setStep('message');
  };
  
  const handleBack = () => {
    if (step === 'amount-selection') {
      setStep('brand-selection');
    } else if (step === 'message') {
      setStep('amount-selection');
    }
  };
  
  const handleSendGift = () => {
    setIsProcessing(true);
    
    setTimeout(() => {
      setIsProcessing(false);
      setStep('confirmation');
    }, 1500);
  };
  
  const handleFinish = () => {
    navigate('/');
    
    const event = new CustomEvent('reset-to-welcome');
    document.getElementById('index-page')?.dispatchEvent(event);
  };
  
  const renderStep = () => {
    switch(step) {
      case 'brand-selection':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Send a Quick Gift to {recipient}</CardTitle>
              <CardDescription>
                {specialDay ? 
                  `It's ${recipient}'s ${specialDay}! Choose one of their favorite brands to send a micro-gift.` :
                  `Choose one of ${recipient}'s favorite brands to send a micro-gift.`
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                {recipientFavoriteBrands.map(brandId => {
                  const brand = BRANDS.find(b => b.id === brandId);
                  if (!brand) return null;
                  
                  return (
                    <Button
                      key={brand.id}
                      variant="outline"
                      className="flex flex-col items-center p-4 h-auto hover:bg-givgo-light hover:text-givgo-purple border-2"
                      onClick={() => handleBrandSelect(brand.id)}
                    >
                      {brand.logo ? (
                        <img 
                          src={brand.logo} 
                          alt={brand.name} 
                          className="w-12 h-12 object-contain mb-2"
                        />
                      ) : (
                        <Gift className="w-12 h-12 mb-2" />
                      )}
                      <span className="text-sm font-medium">{brand.name}</span>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
        
      case 'amount-selection':
        return (
          <Card>
            <CardHeader>
              <CardTitle>Choose Amount</CardTitle>
              <CardDescription>
                How much would you like to send?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                {[5, 10, 15].map((amount) => (
                  <Button 
                    key={amount}
                    variant="outline"
                    className={`h-16 text-xl hover:bg-givgo-light hover:text-givgo-purple ${
                      selectedAmount === amount ? "bg-givgo-light text-givgo-purple border-2 border-givgo-purple" : ""
                    }`}
                    onClick={() => handleAmountSelect(amount as GiftAmount)}
                  >
                    ${amount}
                  </Button>
                ))}
              </div>
            </CardContent>
            <CardFooter>
              <Button
                variant="ghost"
                onClick={handleBack}
                className="mt-4"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
            </CardFooter>
          </Card>
        );
        
      case 'message':
        const selectedBrand = BRANDS.find(b => b.id === selectedBrandId);
        
        return (
          <Card>
            <CardHeader>
              <CardTitle>Add a Message</CardTitle>
              <CardDescription>
                Add a personal message to your gift (optional)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-givgo-light p-4 rounded-lg">
                  <div className="flex items-center mb-2">
                    {selectedBrand?.logo && (
                      <img 
                        src={selectedBrand.logo} 
                        alt={selectedBrand.name} 
                        className="w-8 h-8 object-contain mr-2"
                      />
                    )}
                    <span className="font-medium">{selectedBrand?.name}</span>
                  </div>
                  <div className="text-2xl font-bold text-givgo-purple">${selectedAmount}</div>
                </div>
                
                <div>
                  <textarea
                    placeholder="Write a personal message..."
                    rows={3}
                    className="w-full h-24 resize-none border rounded-md px-3 py-2"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>
                
                <div className="pt-4">
                  <Button 
                    className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
                    onClick={handleSendGift}
                    disabled={isProcessing}
                  >
                    {isProcessing ? 'Processing...' : 'Send Gift'}
                  </Button>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button
                variant="ghost"
                onClick={handleBack}
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
            </CardFooter>
          </Card>
        );
        
      case 'confirmation':
        const confirmedBrand = BRANDS.find(b => b.id === selectedBrandId);
        
        return (
          <Card>
            <CardContent className="text-center pt-8 pb-8">
              <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center text-green-600 mb-4">
                <Check className="h-8 w-8" />
              </div>
              <h2 className="text-2xl font-semibold mb-2">Gift Sent!</h2>
              <p className="text-muted-foreground mb-6">
                You've sent {recipient} a ${selectedAmount} {confirmedBrand?.name} gift card.
              </p>
              <div className="space-y-2">
                <Button 
                  className="w-full bg-givgo-purple hover:bg-givgo-purple/90"
                  onClick={handleFinish}
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        );
    }
  };
  
  return (
    <Layout>
      <div className="max-w-md mx-auto">
        {renderStep()}
      </div>
    </Layout>
  );
};

export default QuickGift;
